import type { Place } from '../types';

export interface TypeRankLegendEntry {
  rank: number;
  typeKey: string;
  label: string;
  color: string;
}

/** Distinct colors for primary place types (cycles if many types). */
export const TYPE_RANK_COLORS = [
  '#F97316',
  '#7C3AED',
  '#EC4899',
  '#0EA5E9',
  '#EAB308',
  '#14B8A6',
  '#DC2626',
  '#6366F1',
  '#84CC16',
  '#F43F5E',
  '#8B5CF6',
  '#06B6D4',
  '#D946EF',
  '#F59E0B',
  '#10B981',
] as const;

/** Google Places primary type when no preference tag exists. */
export function getPrimaryPlaceType(place: Place): string {
  const t = place.types?.[0];
  return typeof t === 'string' && t.length > 0 ? t : 'place';
}

/**
 * Category key for ranked markers/legend: user query tag first
 * (`found_for_preferences[0]`), then legacy `found_for_preference`, then Places `types[0]`.
 */
export function getPlaceQueryTagKey(place: Place): string {
  const prefs = place.found_for_preferences;
  if (Array.isArray(prefs) && prefs.length > 0) {
    const first = prefs[0];
    if (typeof first === 'string' && first.trim().length > 0) {
      return first.trim();
    }
  }
  if (place.found_for_preference?.trim()) {
    return place.found_for_preference.trim();
  }
  return getPrimaryPlaceType(place);
}

export function formatPlaceTypeLabel(typeKey: string): string {
  return typeKey
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * From ranked places only: sort unique query tags alphabetically, assign rank 1..n and a color each.
 */
export function buildTypeRankLegend(rankedPlaces: Place[]): {
  entries: TypeRankLegendEntry[];
  colorByType: Map<string, string>;
  rankByType: Map<string, number>;
} {
  const primary = (p: Place) => getPlaceQueryTagKey(p);
  const uniqueTypes = [...new Set(rankedPlaces.map(primary))].sort((a, b) =>
    a.localeCompare(b)
  );
  const entries: TypeRankLegendEntry[] = uniqueTypes.map((typeKey, i) => ({
    rank: i + 1,
    typeKey,
    label: formatPlaceTypeLabel(typeKey),
    color: TYPE_RANK_COLORS[i % TYPE_RANK_COLORS.length],
  }));
  const colorByType = new Map(entries.map((e) => [e.typeKey, e.color]));
  const rankByType = new Map(entries.map((e) => [e.typeKey, e.rank]));
  return { entries, colorByType, rankByType };
}

/**
 * Stops first (order preserved), then ranked places sorted by query tag then name.
 */
export function orderPlacesForMap(places: Place[]): Place[] {
  const stops: Place[] = [];
  const ranked: Place[] = [];
  for (const p of places) {
    if (p.marker_kind === 'ranked') ranked.push(p);
    else stops.push(p);
  }
  ranked.sort((a, b) => {
    const ta = getPlaceQueryTagKey(a);
    const tb = getPlaceQueryTagKey(b);
    const c = ta.localeCompare(tb);
    if (c !== 0) return c;
    return (a.name || '').localeCompare(b.name || '');
  });
  return [...stops, ...ranked];
}
